import { useEffect, useId, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  BoutonSecondaire,
  Carte,
  Chargement,
  EnteteEcran,
  LigneAlerte,
  MessageErreur,
  Pastille,
  type TonPastille,
} from '../components/ui/Primitives';
import { FormulaireDossier } from '../components/dossier/FormulaireDossier';
import { FicheDossier } from '../components/dossier/FicheDossier';
import { ModaleFicheEntretien } from '../components/commercial/ModaleFicheEntretien';
import { ligneEntretienDuDossier } from '../components/dossier/entretien-du-dossier';
import {
  useChangerStatutDocument,
  useControlerDocumentSigne,
  useDeposerDocumentSigne,
  useGenererDocument,
  useReferentiels,
  useRetirerDocumentSigne,
  type DemandeSuivi,
} from '../lib/useDocuments';
import { SuiviDocumentaire, historiqueConvention } from '../components/documents/SuiviDocumentaire';
import { ValidationConvention } from '../components/documents/ValidationConvention';
import { LignePiece } from '../components/dossier/LignePiece';
import { RefusPiece } from '../components/dossier/RefusPiece';
import { CarteAssiduite } from '../components/dossier/CarteAssiduite';
import { peutLireAssiduiteDuDossier } from '../lib/droits-assiduite';
import { CarteFriseDossier } from '../components/dossier/CarteFriseDossier';
import { CarteEcf } from '../components/dossier/CarteEcf';
import { CartePositionnementPedagogique } from '../components/dossier/CartePositionnementPedagogique';
import { peutLireEva } from '../lib/droits-eva';
import { CarteFinancement } from '../components/dossier/CarteFinancement';
import { CarteModificationsEnAttente } from '../components/dossier/CarteModificationsEnAttente';
import { RefusDocumentSigne } from '../components/dossier/RefusDocumentSigne';
import { VisionneusePiece } from '../components/dossier/VisionneusePiece';
import {
  dossierComplet,
  urlCv,
  useControlerPiece,
  useDeposerPiece,
  useDossier,
  useEnregistrerFicheEntretien,
  useSupprimerFichierPiece,
  type CanalEnvoi,
  type Dossier,
  type IssueEnvoi,
  type MotifSuspensionRelance,
  type DocumentDossier,
  type FichePlacement,
  type ImmersionDuDossier,
  type PieceDossier,
} from '../lib/useApprenants';
import { urlCompteRenduImmersion } from '../lib/useMatching';
import { heureReunion } from '../lib/emargement-affichage';
import { useVerserCompteRendu } from '../lib/useEntretiens';
import { Icone } from '../components/Icone';
import { urlDocumentGenere, urlDocumentSigne } from '../lib/useDocuments';
import { PASTILLE_DOCUMENT, pastilleSignature } from '../lib/pastilles';
import type { StatutDocument } from '../lib/useDocuments';
import { ErreurApi } from '../lib/api';
import { periodePositionnement } from '../lib/immersion-statut';
import { useAuth } from '../auth/useAuth';
import { peutOuvrirDossierOrganisation } from '../lib/droits-repertoire';
import {
  lireDernierDossier,
  memoriserDernierDossier,
  oublierDernierDossier,
} from '../lib/dernierDossier';

/** Rendu d'une date à l'heure de La Réunion (les dates arrivent en UTC). */
function dateCourte(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/**
 * Écran « Dossier apprenant » (CDC §4.3, §4.4, §4.8).
 * Dossier unique partagé entre tous les pôles. Structure reprise de la
 * maquette validée : identité et situation, pièces, mémorisation tuteur et
 * employeur à gauche ; statut, alertes et rémunération à droite.
 *
 * Le même écran sert d'espace au candidat, qui n'y voit que son propre dossier.
 * Ce qui relève de l'instruction — documents réglementaires,
 * contrôle des pièces, alertes internes — lui est masqué : l'API les lui
 * refuse de toute façon, les afficher ne ferait que proposer des boutons qui
 * échouent et exposer le fonctionnement interne du service.
 *
 * Il sert enfin de consultation au pédagogique et au formateur, qui l'ouvrent
 * depuis « Apprenants » pour éviter les ressaisies : ils lisent la situation et
 * l'état des pièces, sans rien déposer ni contrôler. Le pédagogique consulte
 * les fichiers à l'écran ; le formateur ne les ouvre pas (TF-490).
 *
 * Le pôle facturation lit la vue de l'administratif sans y écrire (TF-464) :
 * il consulte les pièces à l'écran pour vérifier les accords de financement,
 * et ne modifie que le bloc Financement.
 */
export function AdmDossierPage(): JSX.Element {
  const { id } = useParams();
  const { hash, key: navigation } = useLocation();
  const { utilisateur, peutAcceder } = useAuth();
  // Ouvert depuis le menu, l'écran n'a pas d'identifiant dans l'URL : on
  // reprend le dernier dossier consulté par cette personne. Lu une seule fois
  // au montage — la mémoire est réécrite pendant la visite, la relire ferait
  // boucler la reprise sur elle-même.
  const [reprise, setReprise] = useState(() => lireDernierDossier(utilisateur?.id));
  const idEffectif = id ?? reprise;
  const { data, isLoading, error } = useDossier(idEffectif);
  const controler = useControlerPiece();
  const deposer = useDeposerPiece();
  const supprimerFichier = useSupprimerFichierPiece();
  const genererCalendrier = useGenererDocument(idEffectif);
  const [editionOuverte, setEditionOuverte] = useState(false);
  const [ficheOuverte, setFicheOuverte] = useState(false);
  const [entretienOuvert, setEntretienOuvert] = useState(false);
  const [documentApercu, setDocumentApercu] = useState<{
    libelle: string;
    nomFichier: string;
    urlBase: string;
  } | null>(null);
  const ficheEntretien = useEnregistrerFicheEntretien();
  const { data: referentiels } = useReferentiels();
  // Consultation d'un fichier : la pièce et le rang du fichier cliqué, pour
  // pouvoir passer du recto au verso sans refermer la fenêtre.
  const [consultation, setConsultation] = useState<{ piece: PieceDossier; index: number } | null>(
    null,
  );
  const [refus, setRefus] = useState<PieceDossier | null>(null);
  // Exemplaire signé rendu par l'antenne : dépôt, contrôle et retrait. Les
  // trois mutations partagent l'identifiant du dossier pour rafraîchir d'un
  // coup le dossier et l'écran des documents réglementaires.
  const deposerSigne = useDeposerDocumentSigne(idEffectif);
  const controlerSigne = useControlerDocumentSigne(idEffectif);
  const retirerSigne = useRetirerDocumentSigne(idEffectif);
  const [refusSigne, setRefusSigne] = useState<DocumentDossier | null>(null);
  // Suivi Vérifié / Validé / Transmis (CDC §6.4) : réservé, comme l'API, à
  // l'écran des documents. Le dernier document touché porte l'éventuel refus.
  const changerStatut = useChangerStatutDocument(idEffectif);
  const [documentSuivi, setDocumentSuivi] = useState<string | null>(null);
  // Versement du compte-rendu au dossier : préalable au dépôt d'un exemplaire
  // signé tant que l'entretien n'a produit aucun document (voir plus bas).
  const verserCompteRendu = useVerserCompteRendu();
  // Les droits viennent de l'API : le front applique la liste reçue, il ne
  // rejoue pas la matrice.
  const gestionAdministrative = peutAcceder('adm-dossier');
  // L'espace candidat est la vue du dossier par son titulaire : un profil qui
  // gère aussi les dossiers (la direction a tous les écrans) lit la vue de
  // gestion, pas « Mon dossier ».
  const espaceCandidat = peutAcceder('app-documents') && !gestionAdministrative;
  // Troisième cas : le pédagogique et le formateur ouvrent le dossier depuis
  // « Apprenants » pour éviter les ressaisies. Ils lisent la situation, mais ne
  // touchent ni au dossier ni aux pièces ; l'ouverture des fichiers suit
  // `accesPieces` (le pédagogique à l'écran, le formateur jamais).
  const consultationSeule = !gestionAdministrative && !espaceCandidat;
  // Le pôle facturation lit les mêmes blocs que l'administratif — employeur,
  // session, immersions, alertes, relances, fiche complète —, sans aucun de
  // ses gestes (TF-464). La direction a les deux écrans.
  const lectureAdministrative = gestionAdministrative || peutAcceder('financing');
  // Les comptes rendus et documents produits se servent par une route gardée
  // par le dossier ou l'écran des documents.
  const documentsOuvrables = gestionAdministrative || peutAcceder('adm-documents');
  // Compte rendu d'immersion : mêmes écrans que sa route.
  const compteRenduImmersionOuvrable =
    gestionAdministrative ||
    peutAcceder('com-matching') ||
    peutAcceder('periode-immersion') ||
    peutAcceder('ped-repertoire');
  const versDossierOrganisation = peutOuvrirDossierOrganisation(peutAcceder);
  // Exemplaire signé : le dépôt suit l'API — dossier ou écran des documents,
  // parce que l'exemplaire arrive souvent au guichet ; le contrôle reste au
  // pôle administratif, déposer n'étant pas valider.
  const peutDeposerSigne = gestionAdministrative || peutAcceder('adm-documents');
  const outilsSigne = {
    peutDeposer: peutDeposerSigne,
    peutControler: peutAcceder('adm-documents'),
    enCours: deposerSigne.isPending || controlerSigne.isPending || retirerSigne.isPending,
    onDeposer: (document: DocumentDossier, fichier: File) =>
      deposerSigne.mutate({ documentId: document.id, fichier }),
    onValider: (document: DocumentDossier) =>
      controlerSigne.mutate({ documentId: document.id, statut: 'VALIDE' as const }),
    onRefuser: (document: DocumentDossier) => setRefusSigne(document),
    onRetirer: (document: DocumentDossier) => retirerSigne.mutate({ documentId: document.id }),
    onConsulter: (document: DocumentDossier) =>
      setDocumentApercu({
        libelle: `${document.libelle} — exemplaire signé`,
        nomFichier: document.signe?.nomFichier ?? 'exemplaire-signe.pdf',
        urlBase: urlDocumentSigne(document.id),
      }),
  };

  // Deux listes, pas deux filtres à l'affichage : le compte-rendu d'entretien
  // et la fiche de suivi ne sont pas des documents réglementaires, ils se cherchent sous « Entretien ».
  const documents = dossierComplet(data)?.documents ?? [];
  const estDocumentEntretien = (type: string) =>
    type === 'COMPTE_RENDU_ENTRETIEN' || type === 'FICHE_SUIVI_ENTRETIEN';
  const compteRendusEntretien = documents.filter((document) =>
    estDocumentEntretien(document.type),
  );
  // Le compte rendu d'entretien en entreprise se consulte sous la période
  // d'immersion, où on le cherche, et par une route ouverte à qui voit déjà
  // l'immersion. Le laisser aussi parmi les documents réglementaires
  // l'afficherait deux fois, dont une sans bouton pour qui n'a pas
  // `adm-documents`.
  const documentsReglementaires = documents.filter(
    (document) =>
      !estDocumentEntretien(document.type) && document.type !== 'EVALUATION_IMMERSION',
  );

  // Mémorisation après chargement abouti, jamais sur la seule URL : un
  // identifiant erroné ou un dossier devenu inaccessible ne doit pas s'installer
  // dans la mémoire et faire échouer chaque ouverture suivante.
  const idCharge =
    data?.vue === 'COMPLETE' ? data.dossier.identite.id : data ? data.fiche.id : undefined;
  useEffect(() => {
    if (idCharge) memoriserDernierDossier(utilisateur?.id, idCharge);
  }, [idCharge, utilisateur?.id]);

  // Lien direct vers le bloc Financement (`#financement`, depuis la liste ou
  // l'en-tête) : le routeur ne défile pas jusqu'à une ancre, et le bloc n'existe
  // qu'une fois le dossier chargé. La clé de navigation rejoue le défilement
  // quand le lien de l'en-tête est cliqué une seconde fois.
  useEffect(() => {
    if (hash !== '#financement' || !idCharge) return;
    document.getElementById('financement')?.scrollIntoView({ block: 'start' });
  }, [hash, navigation, idCharge]);

  // Reprise impossible — dossier supprimé, ou sorti du périmètre de site depuis
  // la dernière visite : on oublie et on retombe sur le choix d'un dossier.
  // L'erreur n'est montrée que si la personne a demandé ce dossier-là.
  useEffect(() => {
    if (!id && reprise && error) {
      oublierDernierDossier(utilisateur?.id);
      setReprise(undefined);
    }
  }, [id, reprise, error, utilisateur?.id]);

  const aAccesListe = peutAcceder('adm-dossier') || peutAcceder('learners');
  // `/adm-dossier` sans identifiant ni dossier à reprendre : la route générique
  // des écrans y mène aussi. La requête est alors désactivée, donc ni chargement
  // ni erreur — sans ce cas, l'écran resterait indéfiniment sur « Chargement ».
  const versInscriptions = peutAcceder('adm-inscriptions');
  if (!idEffectif) {
    return <SansDossier versLaListe={aAccesListe} versInscriptions={versInscriptions} />;
  }
  if (!id && error) {
    return <SansDossier versLaListe={aAccesListe} versInscriptions={versInscriptions} />;
  }

  if (isLoading) return <Chargement libelle="Chargement du dossier…" />;

  if (error) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <MessageErreur
          message={
            error instanceof ErreurApi ? error.message : 'Ce dossier n’a pas pu être chargé.'
          }
        />
        <Link
          to={
            peutAcceder('adm-inscriptions')
              ? '/adm-inscriptions'
              : aAccesListe
                ? '/learners'
                : '/financing'
          }
          className="mt-4 inline-flex text-sm font-semibold text-codialis-sky hover:underline"
        >
          {peutAcceder('adm-inscriptions')
            ? 'Retour aux inscriptions'
            : aAccesListe
              ? 'Retour aux apprenants'
              : 'Retour aux financements'}
        </Link>
      </div>
    );
  }

  if (data?.vue === 'PLACEMENT') {
    return <FicheDePlacement fiche={data.fiche} versDossier={versDossierOrganisation} />;
  }

  const dossier = dossierComplet(data);
  // Réponse aboutie mais sans dossier : anomalie, pas une attente. L'afficher
  // comme un chargement laisserait l'écran tourner indéfiniment.
  if (!dossier) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <MessageErreur message="Ce dossier n’a pas pu être chargé." />
      </div>
    );
  }

  const of = dossier.dossier.mode === 'OF';
  const ligneEntretien = ligneEntretienDuDossier(dossier);

  /**
   * Dépôt de l'exemplaire signé d'un document d'entretien que le dossier ne
   * porte pas encore.
   *
   * Le compte-rendu et la fiche de suivi se composent à la volée depuis
   * l'entretien : tant qu'ils ne sont pas versés au dossier, il n'existe aucun
   * document auquel rattacher un exemplaire signé. Le dépôt verse donc d'abord
   * — l'API rend les deux identifiants — puis attache le fichier à celui que
   * l'on vise. Un seul geste à l'écran : rapporter une version signée suppose
   * déjà que le document existe.
   */
  const deposerSigneEntretien = (cible: 'COMPTE_RENDU' | 'FICHE_SUIVI', fichier: File): void => {
    const entretienId = dossier.entretien?.id;
    if (!entretienId) return;
    verserCompteRendu
      .mutateAsync(entretienId)
      .then((verse) => {
        const documentId = cible === 'FICHE_SUIVI' ? verse.ficheSuiviId : verse.documentId;
        if (documentId) deposerSigne.mutate({ documentId, fichier });
      })
      // Le versement porte déjà son erreur dans le bandeau du bloc : la reprendre
      // ici n'ajouterait rien, mais un rejet non traité ferait tomber la page.
      .catch(() => undefined);
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre={
          espaceCandidat
            ? 'Mon dossier'
            : `Dossier apprenant · ${dossier.identite.prenom} ${dossier.identite.nom}`
        }
        sousTitre={
          gestionAdministrative
            ? `Pôle administratif · dossier unique partagé entre tous les pôles${
                id ? '' : ' · dernier dossier consulté'
              }`
            : lectureAdministrative
              ? `Pôle facturation · consultation du dossier, financement modifiable${
                  id ? '' : ' · dernier dossier consulté'
                }`
            : consultationSeule
              ? 'Consultation · dossier tenu par le pôle administratif'
              : 'Vos pièces justificatives et l’avancement de votre dossier'
        }
        actions={
          gestionAdministrative ? (
            <>
              {/* L'écran ne montre que ce qui fait avancer le dossier : l'état
                  civil complet, le bloc CERFA et le parcours scolaire se
                  relisent ici, avant de générer un document réglementaire. */}
              <BoutonSecondaire onClick={() => setFicheOuverte(true)}>Voir fiche</BoutonSecondaire>
              <BoutonSecondaire onClick={() => setEditionOuverte(true)}>
                Modifier
              </BoutonSecondaire>
              {/* La fiche de recrutement vit sur l'entretien : elle s'ouvre ici
                  avec le formulaire du pôle commercial, pas une copie. Absent
                  tant qu'aucun entretien n'a été planifié — il n'y aurait rien
                  à corriger. */}
              {dossier.entretien && (
                <BoutonSecondaire onClick={() => setEntretienOuvert(true)}>
                  Fiche d’entretien
                </BoutonSecondaire>
              )}
              {peutAcceder('adm-documents') && (
                <>
                  <BoutonSecondaire
                    disabled={genererCalendrier.isPending}
                    onClick={() =>
                      genererCalendrier.mutate(
                        { type: 'CALENDRIER_FORMATION' },
                        {
                          onSuccess: (resultat) =>
                            setDocumentApercu({
                              libelle: resultat.intitule,
                              nomFichier: 'calendrier-de-formation.pdf',
                              urlBase: urlDocumentGenere(resultat.documentId),
                            }),
                        },
                      )
                    }
                  >
                    {genererCalendrier.isPending ? 'Génération…' : 'Calendrier de formation PDF'}
                  </BoutonSecondaire>
                </>
              )}
              {peutAcceder('financing') && <LienFinancement apprenantId={dossier.identite.id} />}
            </>
          ) : lectureAdministrative ? (
            <>
              <BoutonSecondaire onClick={() => setFicheOuverte(true)}>Voir fiche</BoutonSecondaire>
              <LienFinancement apprenantId={dossier.identite.id} />
            </>
          ) : null
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2 min-w-0">
          {/* Une correction sensible attend la direction (TF-437) : rien n'est
              modifié avant sa décision, l'équipe le lit avant l'identité. */}
          {gestionAdministrative && <CarteModificationsEnAttente apprenantId={dossier.identite.id} />}
          <IdentiteEtSituation dossier={dossier} of={of} />

          <Carte
            titre={espaceCandidat ? 'Mes pièces justificatives' : 'Pièces justificatives du dossier'}
            actions={
              <span className="text-xs text-codialis-gray">
                {dossier.completude.deposees}/{dossier.completude.attendues} déposées ·{' '}
                {dossier.completude.validees} validées
              </span>
            }
          >
            {(deposer.error || controler.error || supprimerFichier.error) && (
              <div className="px-4 sm:px-6 pt-3">
                <MessageErreur
                  message={(() => {
                    const erreur = deposer.error ?? controler.error ?? supprimerFichier.error;
                    return erreur instanceof ErreurApi
                      ? erreur.message
                      : 'L’opération n’a pas abouti.';
                  })()}
                />
              </div>
            )}
            {genererCalendrier.error && (
              <div className="px-4 sm:px-6 pt-3">
                <MessageErreur
                  message={
                    genererCalendrier.error instanceof ErreurApi
                      ? genererCalendrier.error.message
                      : 'Le calendrier de formation n’a pas pu être généré.'
                  }
                />
              </div>
            )}
            {/* Le refus passe par une fenêtre, qui porte sa propre erreur :
                celle-ci ne couvre que le dépôt, la validation et le retrait. */}
            {(deposerSigne.error ||
              retirerSigne.error ||
              controlerSigne.error ||
              verserCompteRendu.error) && (
              <div className="px-4 sm:px-6 pt-3">
                <MessageErreur
                  message={(() => {
                    const erreur =
                      deposerSigne.error ??
                      retirerSigne.error ??
                      controlerSigne.error ??
                      verserCompteRendu.error;
                    return erreur instanceof ErreurApi
                      ? erreur.message
                      : 'L’opération sur l’exemplaire signé n’a pas abouti.';
                  })()}
                />
              </div>
            )}
            {/* Un dossier créé hors du parcours d'inscription peut n'avoir
                aucune ligne de pièce : le bloc restait alors vide, sans rien
                dire. On nomme la situation plutôt que de laisser croire à un
                écran cassé. */}
            {dossier.pieces.length === 0 && (
              <p className="px-4 sm:px-6 py-4 text-sm text-codialis-gray">
                Aucune pièce justificative n’est encore attendue sur ce dossier.
              </p>
            )}
            {dossier.pieces.map((piece) => (
              <LignePiece
                key={piece.id}
                piece={piece}
                gestionAdministrative={gestionAdministrative}
                consultationSeule={consultationSeule}
                ouvrirFichiers={dossier.accesPieces.consulter}
                enCours={controler.isPending || deposer.isPending || supprimerFichier.isPending}
                onValider={() => controler.mutate({ pieceId: piece.id, statut: 'VALIDEE' })}
                onVerifier={() => controler.mutate({ pieceId: piece.id, statut: 'VERIFIEE' })}
                onDeposer={(fichier, remplace) =>
                  deposer.mutate({ pieceId: piece.id, fichier, ...(remplace ? { remplace } : {}) })
                }
                onSupprimerFichier={(fichierId) =>
                  supprimerFichier.mutate({ pieceId: piece.id, fichierId })
                }
                onConsulter={(index) => setConsultation({ piece, index })}
                onRefuser={() => setRefus(piece)}
              />
            ))}

            {/* Les documents produits vivent dans le même bloc que les pièces,
                parce que c'est là qu'on les cherche — mais ce sont deux listes
                distinctes : une pièce se dépose et se valide, un document se
                consulte. Le lien de téléchargement passe par l'écran des
                documents réglementaires, garde côté serveur comprise : sans ce
                droit, on affiche la liste sans les liens plutôt que de servir
                un 403 au clic. */}
            {/* Les comptes-rendus d'entretien sont sortis de la liste
                générale : ce sont des pièces d'entretien, versées par le pôle
                commercial, et on les cherche sous ce nom — pas parmi les
                documents réglementaires. */}
            {compteRendusEntretien.length > 0 ? (
              <DocumentsProduits
                titre="Entretien"
                documents={compteRendusEntretien}
                telechargeable={documentsOuvrables}
                exemplaireSigne={outilsSigne}
                onConsulter={(document) =>
                  setDocumentApercu({
                    libelle: document.libelle,
                    nomFichier: document.modele
                      ? `${document.modele.replace(/[/\\?%*:|"<>]/g, '-')}.pdf`
                      : document.type === 'FICHE_SUIVI_ENTRETIEN'
                        ? 'fiche-suivi-entretien.pdf'
                        : 'compte-rendu-entretien.pdf',
                    urlBase: urlDocumentGenere(document.id),
                  })
                }
              />
            ) : dossier.entretien ? (
              <div className="border-t border-gray-100">
                <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 sm:px-6 pt-4">
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                    Entretien
                  </h3>
                </div>
                <div className="divide-y divide-gray-50">
                  <div className="flex flex-wrap items-center gap-3 px-4 sm:px-6 py-3 transition hover:bg-gray-50">
                    <div className="flex min-w-0 flex-1 basis-[calc(100%-2rem)] items-center gap-3 sm:basis-auto">
                      <Icone nom="document" className="h-5 w-5 shrink-0 text-codialis-sky" />
                      <div>
                        <div className="text-sm font-medium text-codialis-navy">
                          Compte-rendu d’entretien
                        </div>
                        <div className="text-[11px] text-codialis-gray">
                          Entretien du {dossier.entretien.dateEntretien ? dateCourte(dossier.entretien.dateEntretien) : '—'}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setDocumentApercu({
                          libelle: 'Compte-rendu d’entretien',
                          nomFichier: 'compte-rendu-entretien.pdf',
                          urlBase: `/api/entretiens/${dossier.entretien?.id}/compte-rendu`,
                        })
                      }
                      className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] shrink-0 items-center gap-1.5 rounded-lg border border-codialis-navy/20 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-codialis-sky"
                    >
                      <Icone nom="oeil" className="h-3.5 w-3.5" />
                      Voir
                    </button>
                    {peutDeposerSigne && (
                      <BoutonDepotSigne
                        libelle="Déposer le signé"
                        enCours={verserCompteRendu.isPending || deposerSigne.isPending}
                        onFichier={(fichier) => deposerSigneEntretien('COMPTE_RENDU', fichier)}
                      />
                    )}
                    <Pastille texte={pastilleSignature(null).texte} ton={pastilleSignature(null).ton} />
                  </div>
                  <div className="flex flex-wrap items-center gap-3 px-4 sm:px-6 py-3 transition hover:bg-gray-50">
                    <div className="flex min-w-0 flex-1 basis-[calc(100%-2rem)] items-center gap-3 sm:basis-auto">
                      <Icone nom="document" className="h-5 w-5 shrink-0 text-codialis-sky" />
                      <div>
                        <div className="text-sm font-medium text-codialis-navy">
                          Fiche de suivi d’entretien
                        </div>
                        <div className="text-[11px] text-codialis-gray">
                          Fiche de suivi · entretien du {dossier.entretien.dateEntretien ? dateCourte(dossier.entretien.dateEntretien) : '—'}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setDocumentApercu({
                          libelle: 'Fiche de suivi d’entretien',
                          nomFichier: 'fiche-suivi-entretien.pdf',
                          urlBase: `/api/entretiens/${dossier.entretien?.id}/fiche-suivi`,
                        })
                      }
                      className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] shrink-0 items-center gap-1.5 rounded-lg border border-codialis-navy/20 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-codialis-sky"
                    >
                      <Icone nom="oeil" className="h-3.5 w-3.5" />
                      Voir
                    </button>
                    {peutDeposerSigne && (
                      <BoutonDepotSigne
                        libelle="Déposer le signé"
                        enCours={verserCompteRendu.isPending || deposerSigne.isPending}
                        onFichier={(fichier) => deposerSigneEntretien('FICHE_SUIVI', fichier)}
                      />
                    )}
                    <Pastille texte={pastilleSignature(null).texte} ton={pastilleSignature(null).ton} />
                  </div>
                </div>
              </div>
            ) : null}
            {documentsReglementaires.length > 0 && (
              <DocumentsProduits
                titre="Documents produits par la plateforme"
                apprenantId={idEffectif}
                documents={documentsReglementaires}
                telechargeable={peutAcceder('adm-documents')}
                exemplaireSigne={outilsSigne}
                {...(peutAcceder('adm-documents')
                  ? {
                      suiviDocument: {
                        enCours: changerStatut.isPending,
                        documentId: documentSuivi,
                        erreur:
                          changerStatut.error instanceof ErreurApi
                            ? changerStatut.error.message
                            : null,
                        onAvancer: (document, demande) => {
                          setDocumentSuivi(document.id);
                          changerStatut.mutate({ documentId: document.id, ...demande });
                        },
                      },
                    }
                  : {})}
                onConsulter={(document) =>
                  setDocumentApercu({
                    libelle: document.libelle,
                    nomFichier: document.modele
                      ? `${document.modele.replace(/[/\\?%*:|"<>]/g, '-')}.pdf`
                      : `${document.type.toLowerCase()}.pdf`,
                    urlBase: urlDocumentGenere(document.id),
                  })
                }
              />
            )}
          </Carte>

          {/* Le mode ne fait pas que changer des libellés : en OF le parcours ne
              passe pas par l'entreprise, donc ni tuteur ni période d'essai. Les
              champs propres à l'apprentissage sont masqués, pas laissés vides
              (CDC §2.2). */}
          {lectureAdministrative &&
            (of ? (
              <SessionDeFormation dossier={dossier} versSessions={peutAcceder('ped-sessions')} />
            ) : (
              <MemorisationTuteurEmployeur dossier={dossier} versDossier={versDossierOrganisation} />
            ))}

          {/* La période d'immersion est un objet du parcours en apprentissage :
              en OF il n'y en a pas, et le bloc disparaît plutôt que de rester
              vide (CDC §2.2). Le compte rendu d'entretien s'y consulte sans
              quitter le dossier — c'est le même document que celui du dossier
              de l'entreprise. */}
          {lectureAdministrative && dossier.immersions.length > 0 && (
            <PeriodesImmersion
              immersions={dossier.immersions}
              {...(compteRenduImmersionOuvrable
                ? {
                    onVoirCompteRendu: (immersion: ImmersionDuDossier) =>
                      setDocumentApercu({
                        libelle: 'Compte rendu d’entretien en entreprise',
                        nomFichier: `compte-rendu-entretien-entreprise-${immersion.id}.pdf`,
                        urlBase: urlCompteRenduImmersion(immersion.id),
                      }),
                  }
                : {})}
            />
          )}

          {/* « Ce candidat vient-il ? » se pose devant le dossier, pas dans
              l'écran des absences : avant d'établir un contrat, et quand
              l'employeur ou l'OPCO le demande. La carte lit l'émargement déjà
              recueilli — elle ne tient aucun compteur à elle. Elle reste hors
              de l'espace candidat : l'apprenant relit son assiduité dans
              « Mon émargement », avec le pavé pour signer. */}
          {peutLireAssiduiteDuDossier(peutAcceder, espaceCandidat) && (
            <CarteAssiduite apprenantId={dossier.identite.id} />
          )}

          {/* Le financement relève du pôle facturation (TF-404) : la carte ne se
              monte que pour qui a « Financeurs & OPCO » — facturation et
              direction —, et l'API la refuse aux autres pôles, administratif
              compris. Un parcours OF y a son accord de financement (TF-417).
              L'ancre `#financement` y mène directement (TF-464). */}
          {peutAcceder('financing') && (
            <div id="financement" className="scroll-mt-24">
              <CarteFinancement apprenantId={dossier.identite.id} />
            </div>
          )}

          {/* La frise se lit par toute l'équipe qui ouvre le dossier ;
              l'apprenant a la sienne, sans les alertes des pôles, dans
              « Ma timeline ». */}
          {/* Les ECF se saisissent dans « Fin de formation & ECF » ; le dossier
              les relit bloc par bloc, pour l'équipe qui prépare l'examen. */}
          {!espaceCandidat && <CarteEcf apprenantId={dossier.identite.id} />}

          {/* Le test EVA et les tests de positionnement se lisent ensemble
              (TF-441), par le pôle pédagogique et la direction seuls. */}
          {peutLireEva(utilisateur?.profil) && (
            <CartePositionnementPedagogique apprenantId={dossier.identite.id} />
          )}

          {!espaceCandidat && <CarteFriseDossier apprenantId={dossier.identite.id} />}
        </div>

        <div className="space-y-6">
          {/* Avant toute chose : ce dossier a-t-il déjà une entreprise ? La
              question se pose à chaque pôle — la relation entreprise avant de
              lancer une recherche, l'administratif avant d'établir un contrat —
              et la réponse ramène au dossier de l'organisation, sans ressaisie. */}
          <EntrepriseRattachee
            entreprise={dossier.entreprise}
            immersions={dossier.immersions}
            rechercheEntreprise={dossier.dossier.rechercheEntreprise}
            rechercheDepuisLe={dossier.dossier.rechercheDepuisLe}
            of={of}
            versDossier={versDossierOrganisation}
          />
          <StatutDuDossier dossier={dossier} of={of} />
          {/* Les alertes sont des relances de service, adressées aux pôles :
              elles n'ont pas à s'afficher dans l'espace du candidat. */}
          {lectureAdministrative && <AlertesActives dossier={dossier} />}
          {/* Même raison que les alertes : la mécanique de relance est un outil
              de service. Le candidat reçoit les messages, il n'a pas à voir le
              calendrier interne qui les déclenche. */}
          {lectureAdministrative && <RelancesDuDossier dossier={dossier} />}
          <Remuneration dossier={dossier} />
        </div>
      </div>

      {gestionAdministrative && (
        <FormulaireDossier
          ouvert={editionOuverte}
          dossier={dossier}
          onFermer={() => setEditionOuverte(false)}
        />
      )}

      {/* Montée à l'ouverture seulement : la fiche réunit des données sensibles
          — NIR, INE, représentant légal — qui n'ont pas à rester dans l'arbre
          une fois la fenêtre refermée. */}
      {lectureAdministrative && ficheOuverte && (
        <FicheDossier
          dossier={dossier}
          onFermer={() => setFicheOuverte(false)}
          onVoirCompteRendu={
            dossier.entretien
              ? () =>
                  setDocumentApercu({
                    libelle: 'Compte-rendu d’entretien',
                    nomFichier: 'compte-rendu-entretien.pdf',
                    urlBase: `/api/entretiens/${dossier.entretien?.id}/compte-rendu`,
                  })
              : undefined
          }
        />
      )}

      {gestionAdministrative && entretienOuvert && ligneEntretien && (
        <ModaleFicheEntretien
          entretien={ligneEntretien}
          referentiel={dossier.referentielFiche}
          formations={referentiels?.formations ?? []}
          // Le mode et la formation du dossier se modifient par « Modifier » :
          // deux chemins d'écriture pour la même donnée finiraient par se
          // contredire.
          peutModifierDossier={false}
          enCours={ficheEntretien.isPending}
          erreur={
            ficheEntretien.error instanceof ErreurApi ? ficheEntretien.error.message : null
          }
          onEnregistrer={(valeurs) => {
            if (!idEffectif) return;
            ficheEntretien.mutate(
              { id: idEffectif, fiche: valeurs.fiche },
              {
                onSuccess: () => {
                  ficheEntretien.reset();
                  setEntretienOuvert(false);
                },
              },
            );
          }}
          onFermer={() => {
            ficheEntretien.reset();
            setEntretienOuvert(false);
          }}
        />
      )}

      {/* La visionneuse n'est montée qu'à l'ouverture : elle garde le contenu
          d'une pièce justificative en mémoire, il n'a pas à y rester après
          fermeture. */}
      {consultation && (
        <VisionneusePiece
          ouverte
          libellePiece={consultation.piece.libelle}
          fichiers={consultation.piece.fichiers}
          indexInitial={consultation.index}
          pieceId={consultation.piece.id}
          telechargeable={consultation.piece.telechargeable}
          onFermer={() => setConsultation(null)}
        />
      )}

      {documentApercu && (
        <VisionneusePiece
          ouverte
          libellePiece={documentApercu.libelle}
          nomFichier={documentApercu.nomFichier}
          urlBase={documentApercu.urlBase}
          onFermer={() => setDocumentApercu(null)}
        />
      )}

      {refus && (
        <RefusPiece
          ouverte
          libellePiece={refus.libelle}
          enCours={controler.isPending}
          erreur={controler.error instanceof ErreurApi ? controler.error.message : null}
          onRefuser={(motif) =>
            controler.mutate(
              { pieceId: refus.id, statut: 'REFUSEE', motifRefus: motif },
              { onSuccess: () => setRefus(null) },
            )
          }
          onFermer={() => {
            controler.reset();
            setRefus(null);
          }}
        />
      )}

      {refusSigne && (
        <RefusDocumentSigne
          ouverte
          libelleDocument={refusSigne.libelle}
          enCours={controlerSigne.isPending}
          erreur={controlerSigne.error instanceof ErreurApi ? controlerSigne.error.message : null}
          onRefuser={(motif) =>
            controlerSigne.mutate(
              { documentId: refusSigne.id, statut: 'REFUSE', motifRefus: motif },
              { onSuccess: () => setRefusSigne(null) },
            )
          }
          onFermer={() => {
            controlerSigne.reset();
            setRefusSigne(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Documents produits par la plateforme, listés sous les pièces justificatives.
 *
 * Séparés des pièces par un filet et un intitulé : une pièce est déposée par
 * l'apprenant et se valide, un document est généré et se consulte. Les mélanger
 * dans la même liste ferait croire qu'un formulaire de rupture est une pièce
 * manquante au dossier.
 *
 * `telechargeable` reflète le droit `adm-documents`, que la route de
 * téléchargement vérifie elle-même : sans lui, la liste reste visible — savoir
 * qu'un document existe fait partie du dossier — mais sans lien qui finirait
 * en 403.
 */
/**
 * Outils du dépôt d'un exemplaire signé, passés par l'écran qui en a le droit.
 *
 * Absents, la liste reste ce qu'elle était : des documents produits qu'on
 * consulte. Le bloc « Entretien » n'en reçoit pas — un compte rendu d'entretien
 * ne se renvoie pas signé.
 */
/** Suivi Vérifié / Validé / Transmis, pour qui a l'écran des documents (`adm-documents`). */
export interface OutilsSuiviDocument {
  enCours: boolean;
  /** Document du dernier geste : c'est sous lui que s'affiche un refus de l'API. */
  documentId: string | null;
  erreur: string | null;
  onAvancer: (document: DocumentDossier, demande: Omit<DemandeSuivi, 'documentId'>) => void;
}

export interface OutilsExemplaireSigne {
  /** Dépôt et retrait : `adm-dossier` ou `adm-documents`, comme l'API. */
  peutDeposer: boolean;
  /** Contrôle réservé au pôle administratif : déposer n'est pas valider. */
  peutControler: boolean;
  enCours: boolean;
  onDeposer: (document: DocumentDossier, fichier: File) => void;
  onValider: (document: DocumentDossier) => void;
  onRefuser: (document: DocumentDossier) => void;
  onRetirer: (document: DocumentDossier) => void;
  onConsulter: (document: DocumentDossier) => void;
}

function DocumentsProduits({
  titre,
  apprenantId,
  documents,
  telechargeable,
  onConsulter,
  exemplaireSigne,
  suiviDocument,
}: {
  titre: string;
  /** Dossier des documents : la convention en a besoin pour ses gestes de validation. */
  apprenantId?: string;
  documents: DocumentDossier[];
  telechargeable: boolean;
  onConsulter?: (document: DocumentDossier) => void;
  exemplaireSigne?: OutilsExemplaireSigne;
  suiviDocument?: OutilsSuiviDocument;
}): JSX.Element {
  // Le bloc « Entretien » suit la même garde : sa route est celle des
  // documents produits, fermée aux pôles qui consultent le dossier.
  const accesAutorise = telechargeable;
  // Le dépôt suppose de pouvoir ouvrir ce qu'on dépose : sans accès au fichier,
  // les boutons mèneraient à un 403 que l'API sert déjà.
  const outils = accesAutorise ? exemplaireSigne : undefined;

  return (
    <div className="border-t border-gray-100">
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 sm:px-6 pt-4">
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
          {titre}
        </h3>
        {!accesAutorise && (
          <span className="text-[11px] text-codialis-gray">
            Consultation réservée au pôle administratif
          </span>
        )}
      </div>

      <ul className="mt-1 divide-y divide-gray-50">
        {documents.map((document) => (
          <li
            key={document.id}
            className="px-4 sm:px-6 py-3 transition hover:bg-gray-50"
          >
            <div className="flex flex-wrap items-center gap-3">
              <Icone nom="document" className="h-5 w-5 shrink-0 text-codialis-sky" />
              {/* En mobile, l'intitulé prend sa propre ligne : deux actions et
                  une pastille à côté le rogneraient à « Co… ». Il reprend sa
                  place dans la ligne dès que la largeur le permet. */}
              <div className="min-w-0 basis-[calc(100%-2rem)] sm:flex-1 sm:basis-auto">
                <div className="truncate text-sm font-medium text-codialis-navy">
                  {document.libelle}
                </div>
                <div className="text-[11px] text-codialis-gray">
                  Généré le {dateCourte(document.genereLe)}
                  {document.modele ? ` · modèle ${document.modele}` : ''}
                </div>
              </div>
              {accesAutorise && (
                onConsulter ? (
                  <button
                    type="button"
                    onClick={() => onConsulter(document)}
                    className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] shrink-0 items-center gap-1.5 rounded-lg border border-codialis-navy/20 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-codialis-sky"
                  >
                    <Icone nom="oeil" className="h-3.5 w-3.5" />
                    Ouvrir
                  </button>
                ) : (
                  <a
                    href={urlDocumentGenere(document.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] shrink-0 items-center gap-1.5 rounded-lg border border-codialis-navy/20 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-codialis-sky"
                  >
                    <Icone nom="oeil" className="h-3.5 w-3.5" />
                    Ouvrir
                  </a>
                )
              )}
              {/* Le dépôt reste ouvert tant que l'exemplaire n'est pas validé :
                  un exemplaire refusé doit pouvoir être remplacé sans passer
                  par un retrait. L'API applique la même règle. */}
              {outils?.peutDeposer &&
                document.signable !== false &&
                document.signe?.statut !== 'VALIDE' &&
                !document.convention?.refusSignature && (
                <BoutonDepotSigne
                  libelle={document.signe ? 'Remplacer le signé' : 'Déposer le signé'}
                  enCours={outils.enCours}
                  onFichier={(fichier) => outils.onDeposer(document, fichier)}
                />
              )}
              {/* Une convention dit son état dans le circuit de validation
                  de la direction (TF-430) : « Vérifié » y voudrait dire
                  « à valider ». */}
              <Pastille
                texte={document.convention?.libelle ?? PASTILLE_DOCUMENT[document.statut as StatutDocument].texte}
                ton={document.convention?.ton ?? PASTILLE_DOCUMENT[document.statut as StatutDocument].ton}
              />
              {/* État de signature, à côté de l'état du document : ce sont deux
                  choses distinctes — un document peut être validé par le pôle
                  administratif et attendre encore son exemplaire signé. La
                  pastille est là dès l'origine, « Non signé », parce qu'une
                  pastille absente se confond avec « pas concerné ».
                  Elle n'apparaît que pour qui peut ouvrir l'exemplaire : sans
                  ce droit, l'état d'un fichier qu'on ne verra pas n'apprend
                  rien. */}
              {outils && document.signable !== false && !document.convention?.refusSignature && (
                <Pastille
                  texte={pastilleSignature(document.signe?.statut).texte}
                  ton={pastilleSignature(document.signe?.statut).ton}
                />
              )}
            </div>

            <div className="sm:ml-8">
              {document.convention && apprenantId && (
                <ValidationConvention
                  documentId={document.id}
                  apprenantId={apprenantId}
                  convention={document.convention}
                  peutSoumettre={suiviDocument !== undefined}
                />
              )}
              <SuiviDocumentaire
                suivi={document.suivi}
                peutAvancer={suiviDocument !== undefined}
                enCours={suiviDocument?.enCours ?? false}
                erreur={suiviDocument?.documentId === document.id ? suiviDocument.erreur : null}
                onAvancer={(demande) => suiviDocument?.onAvancer(document, demande)}
                {...(document.convention
                  ? { historique: historiqueConvention(document.suivi, document.convention) }
                  : {})}
              />
            </div>

            {/* L'exemplaire signé rendu par l'antenne, sous le document dont il
                dépend : ce n'est pas un document de plus dans la liste, c'est
                un état de celui-ci, avec son propre contrôle. */}
            {outils && document.signe && (
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg bg-codialis-sand px-3 py-2 sm:ml-8">
                <button
                  type="button"
                  onClick={() => outils.onConsulter(document)}
                  // `basis-full` : le nom passe sur sa propre ligne plutôt que
                  // d'être rogné à une lettre par les quatre actions qui le
                  // suivent. Il reprend sa place dans la ligne dès que la
                  // largeur le permet, sans jamais descendre sous 12 rem.
                  className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] basis-full items-center gap-2 rounded-lg px-1 text-left text-xs font-medium text-codialis-navy transition hover:text-codialis-sky hover:underline focus-visible:ring-2 focus-visible:ring-codialis-sky lg:min-w-[12rem] lg:flex-1 lg:basis-auto"
                  aria-label={`Ouvrir l’exemplaire signé ${document.signe.nomFichier}`}
                >
                  <Icone nom="oeil" className="h-5 w-5 shrink-0 text-codialis-gray" />
                  <span className="truncate">{document.signe.nomFichier}</span>
                </button>
                {/* L'état de signature est porté par la ligne du document
                    au-dessus : le répéter ici en ferait deux pastilles à tenir
                    d'accord. Ne reste que ce qui est propre au fichier. */}
                <span className="text-[11px] text-codialis-gray">
                  Exemplaire signé · déposé le {dateCourte(document.signe.deposeLe)}
                </span>
                {outils.peutControler && document.signe.statut !== 'VALIDE' && (
                  <button
                    type="button"
                    onClick={() => outils.onValider(document)}
                    disabled={outils.enCours}
                    className="min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg bg-codialis-mint/20 px-3 py-2 text-[11px] font-semibold text-codialis-accent-texte transition hover:bg-codialis-mint/30 disabled:opacity-50"
                  >
                    Valider
                  </button>
                )}
                {/* Refuser exige un motif : sans lui, le même fichier revient. */}
                {outils.peutControler && document.signe.statut !== 'REFUSE' && (
                  <button
                    type="button"
                    onClick={() => outils.onRefuser(document)}
                    disabled={outils.enCours}
                    className="min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg border border-red-200 px-3 py-2 text-[11px] font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-50"
                  >
                    Refuser
                  </button>
                )}
                {/* Un exemplaire validé ne se retire pas d'un clic : l'API le
                    refuse, il faut le refuser d'abord, motif à l'appui. */}
                {outils.peutDeposer && document.signe.statut !== 'VALIDE' && (
                  <button
                    type="button"
                    onClick={() => outils.onRetirer(document)}
                    disabled={outils.enCours}
                    className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] min-w-[38px] [@media(pointer:coarse)]:min-w-[40px] items-center justify-center rounded-lg border border-transparent text-codialis-gray transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus-visible:ring-2 focus-visible:ring-codialis-sky disabled:opacity-50"
                    aria-label={`Retirer l’exemplaire signé ${document.signe.nomFichier}`}
                  >
                    <Icone nom="corbeille" className="h-5 w-5" />
                  </button>
                )}
                {document.signe.motifRefus && (
                  <p className="w-full text-[11px] text-red-700">
                    Motif du refus : {document.signe.motifRefus}
                  </p>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Dépôt de l'exemplaire signé : un `label` associé à un `input file` masqué,
 * comme pour les pièces justificatives — la zone reste un vrai contrôle de
 * formulaire, atteignable au clavier et annoncé aux lecteurs d'écran.
 *
 * Seul le PDF est accepté, et le contrôle est refait côté API, signature
 * binaire du contenu comprise : celui-ci n'évite que l'attente d'un
 * téléversement voué au refus.
 */
function BoutonDepotSigne({
  libelle,
  enCours,
  onFichier,
}: {
  libelle: string;
  enCours: boolean;
  onFichier: (fichier: File) => void;
}): JSX.Element {
  const champ = useId();
  const [refus, setRefus] = useState<string | null>(null);

  return (
    <div className="flex flex-col items-end gap-1">
      <label
        htmlFor={champ}
        title="Format accepté : PDF · 10 Mo max"
        className={`inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] cursor-pointer items-center gap-1.5 rounded-lg border border-codialis-navy/20 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-gray-50 focus-within:ring-2 focus-within:ring-codialis-sky ${
          enCours ? 'pointer-events-none opacity-50' : ''
        }`}
      >
        <Icone nom="televerser" className="h-3.5 w-3.5" />
        {libelle}
        <input
          id={champ}
          type="file"
          className="sr-only"
          accept="application/pdf"
          disabled={enCours}
          onChange={(evenement) => {
            const fichier = evenement.target.files?.[0];
            // Champ remis à zéro : redéposer le même fichier après un refus
            // doit bien déclencher un nouvel événement.
            evenement.target.value = '';
            if (!fichier) return;
            if (fichier.size > 10 * 1024 * 1024) {
              setRefus('Fichier trop lourd : 10 Mo au maximum.');
              return;
            }
            if (fichier.type && fichier.type !== 'application/pdf') {
              setRefus('Format refusé. L’exemplaire signé doit être un PDF.');
              return;
            }
            setRefus(null);
            onFichier(fichier);
          }}
        />
      </label>
      {refus && (
        <span role="alert" className="max-w-[16rem] text-right text-[11px] text-red-600">
          {refus}
        </span>
      )}
    </div>
  );
}

function IdentiteEtSituation({
  dossier,
  of,
}: {
  dossier: Dossier;
  of: boolean;
}): JSX.Element {
  const champs: [string, React.ReactNode][] = [
    ['Nom / Prénom', `${dossier.identite.nom} ${dossier.identite.prenom}`],
    [
      'Date de naissance',
      dossier.identite.dateNaissance
        ? `${dateCourte(dossier.identite.dateNaissance)}${
            dossier.identite.age === null ? '' : ` (${dossier.identite.age} ans)`
          }`
        : '—',
    ],
    ['Site de rattachement', dossier.dossier.site.code],
    [
      'Formation',
      dossier.dossier.formation
        ? `${dossier.dossier.formation.titre} · RNCP ${dossier.dossier.formation.rncp}${
            of && dossier.dossier.session ? ` (${dossier.dossier.session.libelle})` : ''
          }`
        : of && dossier.dossier.session
          ? dossier.dossier.session.libelle
          : of
            ? 'Session de formation à créer'
            : '—',
    ],
    ['Statut', dossier.dossier.statutLibelle],
    [
      'Conseiller référent',
      dossier.dossier.conseiller
        ? `${dossier.dossier.conseiller.prenom} ${dossier.dossier.conseiller.nom}`
        : '—',
    ],
    ['Email', dossier.identite.email ?? '—'],
    ['Téléphone', dossier.identite.telephone ?? '—'],
    ['Adresse', [dossier.identite.codePostal, dossier.identite.ville].filter(Boolean).join(' ') || '—'],
  ];

  return (
    <Carte titre="Identité & situation" sansBordure>
      <div className="grid grid-cols-1 gap-4 p-4 sm:p-6 sm:grid-cols-2">
        {champs.map(([libelle, valeur]) => (
          <div key={libelle}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
              {libelle}
            </div>
            <div className="mt-0.5 text-sm font-medium text-codialis-navy">{valeur}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 border-t border-gray-100 px-4 sm:px-6 py-4">
        <Pastille
          texte={dossier.situation.mineur ? 'Mineur' : 'Majeur'}
          ton={dossier.situation.mineur ? 'warning' : 'neutral'}
        />
        {dossier.situation.rqth && (
          <Pastille texte="RQTH · aménagement d’examen à instruire" ton="info" />
        )}
        {dossier.situation.hebergeParTiers && (
          <Pastille texte="Hébergé par un tiers" ton="neutral" />
        )}
        {dossier.situation.demandeurEmploi && (
          <Pastille texte="Inscrit à France Travail" ton="neutral" />
        )}
        {!dossier.situation.titulaireDiplome && <Pastille texte="Sans diplôme" ton="neutral" />}
        <Pastille
          texte={of ? 'Organisme de Formation' : 'CFA · apprentissage'}
          ton={of ? 'mint' : 'info'}
        />
      </div>
    </Carte>
  );
}

/**
 * Entreprise rattachée au dossier, et chemin vers son dossier.
 *
 * Deux rattachements possibles, dans cet ordre : le **contrat** — l'entreprise
 * d'accueil, celle du CERFA — puis, à défaut, la **période d'immersion** la plus
 * récente, qui dit qu'un positionnement est en cours sans qu'un contrat existe.
 * Les deux mènent au même endroit, `/ped-repertoire/:id`, avec le geste
 * standardisé de l'application : le nom et « Ouvrir le dossier » y vont pareil.
 *
 * En mode OF le parcours ne passe pas par l'entreprise : le bloc disparaît
 * plutôt que d'annoncer une absence qui n'a pas de sens (CDC §2.2).
 */
function EntrepriseRattachee({
  entreprise,
  immersions,
  rechercheEntreprise,
  rechercheDepuisLe,
  of,
  versDossier,
}: {
  entreprise: { id: string; denomination: string; siret?: string; ville?: string | null } | null;
  immersions: ImmersionDuDossier[];
  rechercheEntreprise: boolean;
  rechercheDepuisLe: string | null;
  of: boolean;
  versDossier: boolean;
}): JSX.Element | null {
  if (of) return null;

  const contrat = entreprise;
  // Les immersions arrivent de l'API triées de la plus récente à la plus
  // ancienne : la première est celle qui renseigne sur la situation du jour.
  const immersion = immersions[0] ?? null;
  const rattachement = contrat
    ? {
        id: contrat.id,
        nom: contrat.denomination,
        detail: [contrat.siret ? `SIRET ${contrat.siret}` : null, contrat.ville]
          .filter(Boolean)
          .join(' · '),
        pastille: { texte: 'Contrat', ton: 'success' as TonPastille },
        origine: 'Entreprise d’accueil du contrat',
      }
    : immersion
      ? {
          id: immersion.entrepriseId,
          nom: immersion.entreprise,
          detail: periodePositionnement(immersion, dateCourte),
          pastille: immersion.sansImmersion
            ? { texte: 'Positionnement', ton: 'mint' as TonPastille }
            : { texte: 'Immersion', ton: 'info' as TonPastille },
          origine: immersion.sansImmersion
            ? 'Positionnement sans immersion, contrat à établir'
            : 'Positionnement en cours, sans contrat',
        }
      : null;

  return (
    <Carte titre="Entreprise rattachée">
      {rattachement ? (
        <div className="space-y-3 p-4 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-codialis-sand text-codialis-navy">
                <Icone nom="batiment" className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                {versDossier ? (
                  <Link
                    to={`/ped-repertoire/${rattachement.id}`}
                    className="block truncate text-sm font-semibold text-codialis-navy transition hover:text-codialis-sky hover:underline"
                  >
                    {rattachement.nom}
                  </Link>
                ) : (
                  <div className="truncate text-sm font-semibold text-codialis-navy">
                    {rattachement.nom}
                  </div>
                )}
                {rattachement.detail && (
                  <div className="mt-0.5 text-[11px] text-codialis-gray">{rattachement.detail}</div>
                )}
              </div>
            </div>
            <Pastille texte={rattachement.pastille.texte} ton={rattachement.pastille.ton} />
          </div>
          <div className="text-[11px] text-codialis-gray">{rattachement.origine}</div>
          {versDossier && (
            <Link
              to={`/ped-repertoire/${rattachement.id}`}
              className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] w-full items-center justify-center gap-1.5 rounded-lg border border-codialis-navy/20 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              <Icone nom="lien" className="h-3.5 w-3.5" />
              Ouvrir le dossier de l’entreprise
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-2 p-4 sm:p-6 text-sm text-codialis-gray">
          <p>Aucune entreprise rattachée : ni contrat, ni période d’immersion.</p>
          {rechercheEntreprise && (
            <p className="text-[11px]">
              Candidat en recherche d’entreprise
              {rechercheDepuisLe ? ` depuis le ${dateCourte(rechercheDepuisLe)}` : ''}
              {' '}· le positionnement se fait depuis le matching commercial.
            </p>
          )}
        </div>
      )}
    </Carte>
  );
}

function MemorisationTuteurEmployeur({
  dossier,
  versDossier,
}: {
  dossier: Dossier;
  /** Même garde que le bloc « Entreprise rattachée » : sans droit, pas de lien. */
  versDossier: boolean;
}): JSX.Element {
  return (
    <Carte titre="Mémorisation tuteur & employeur" sansBordure>
      {!dossier.entreprise && !dossier.tuteur ? (
        <p className="p-4 sm:p-6 text-sm text-codialis-gray">
          Aucune entreprise rattachée pour l’instant. Dès qu’un contrat est établi, les
          informations de l’employeur et du maître d’apprentissage sont conservées ici.
        </p>
      ) : (
        <div className="space-y-4 p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-xl bg-codialis-sand p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                Entreprise d’accueil
              </div>
              <div className="mt-1 text-sm font-semibold text-codialis-navy">
                {dossier.entreprise && versDossier ? (
                  <Link
                    to={`/ped-repertoire/${dossier.entreprise.id}`}
                    className="transition hover:text-codialis-sky hover:underline"
                  >
                    {dossier.entreprise.denomination}
                  </Link>
                ) : (
                  (dossier.entreprise?.denomination ?? '—')
                )}
              </div>
              <div className="mt-0.5 text-xs text-codialis-gray">
                SIRET {dossier.entreprise?.siret ?? '—'}
                {dossier.entreprise?.ville ? ` · ${dossier.entreprise.ville}` : ''}
              </div>
              <div className="mt-0.5 text-xs text-codialis-gray">
                {dossier.entreprise?.opco ?? 'OPCO à renseigner'}
                {dossier.entreprise?.idcc ? ` · IDCC ${dossier.entreprise.idcc}` : ''}
              </div>
            </div>

            <div className="rounded-xl bg-codialis-sand p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                Maître d’apprentissage
              </div>
              <div className="mt-1 text-sm font-semibold text-codialis-navy">
                {dossier.tuteur ? `${dossier.tuteur.prenom} ${dossier.tuteur.nom}` : '—'}
              </div>
              <div className="mt-0.5 text-xs text-codialis-gray">
                {dossier.tuteur?.emploiOccupe ?? '—'}
                {dossier.tuteur?.anneesExperience
                  ? ` · ${dossier.tuteur.anneesExperience} ans d’expérience`
                  : ''}
              </div>
              <div className="mt-0.5 text-xs text-codialis-gray">
                {[dossier.tuteur?.email, dossier.tuteur?.telephone].filter(Boolean).join(' · ') ||
                  '—'}
              </div>
            </div>
          </div>

          <p className="rounded-xl border border-codialis-mint/40 bg-codialis-mint/10 p-4 text-xs text-codialis-navy">
            Ces informations sont <strong>conservées en mémoire</strong> et réinjectées
            automatiquement dans les prochains contrats liés à cette entreprise, sans
            ressaisie.
          </p>
        </div>
      )}
    </Carte>
  );
}

/**
 * Session de formation du dossier, à la place du bloc tuteur / employeur du
 * mode CFA : c'est elle qui porte les dates du parcours en mode OF, et le
 * certificat de réalisation qui le clôt (CDC §2.2).
 */
function SessionDeFormation({
  dossier,
  versSessions,
}: {
  dossier: Dossier;
  /** Lien vers les sessions, pour qui a l'écran : sans lui, il mènerait à un refus. */
  versSessions: boolean;
}): JSX.Element {
  const session = dossier.dossier.session;

  return (
    <Carte titre="Session de formation" sansBordure>
      {!session ? (
        <div className="space-y-3 p-4 sm:p-6">
          <p className="text-sm text-codialis-gray">
            Ce dossier est en mode OF : son parcours passe par une session de formation, pas
            par un contrat d’apprentissage. Aucune session ne lui est rattachée pour
            l’instant, et le certificat de réalisation ne peut pas être produit sans elle.
          </p>
          {versSessions && (
            <Link
              to="/ped-sessions"
              className="inline-flex rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              Ouvrir les sessions
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4 p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-xl bg-codialis-sand p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                Session rattachée
              </div>
              <div className="mt-1 text-sm font-semibold text-codialis-navy">
                {session.libelle}
              </div>
              <div className="mt-0.5 text-xs text-codialis-gray">
                {dossier.dossier.formation
                  ? `${dossier.dossier.formation.titre} · RNCP ${dossier.dossier.formation.rncp}`
                  : 'Formation à renseigner'}
              </div>
            </div>

            <div className="rounded-xl bg-codialis-sand p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                Période
              </div>
              <div className="mt-1 text-sm font-semibold text-codialis-navy">
                {dateCourte(session.dateDebut)} → {dateCourte(session.dateFin)}
              </div>
              <div className="mt-0.5 text-xs text-codialis-gray">
                Antenne {dossier.dossier.site.code}
              </div>
            </div>
          </div>

          <p className="rounded-xl border border-codialis-mint/40 bg-codialis-mint/10 p-4 text-xs text-codialis-navy">
            En fin de parcours, le <strong>certificat de réalisation</strong> se génère depuis
            les documents réglementaires. Il n’atteste que les heures constatées par
            émargement sur cette session.
          </p>
        </div>
      )}
    </Carte>
  );
}

function StatutDuDossier({ dossier, of }: { dossier: Dossier; of: boolean }): JSX.Element {
  return (
    <Carte titre="Statut du dossier" sansBordure>
      <div className="space-y-4 p-4 sm:p-6">
        <div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-codialis-gray">Complétude</span>
            <span className="font-semibold text-codialis-navy">
              {dossier.completude.deposees}/{dossier.completude.attendues} pièces
            </span>
          </div>
          <div className="progress-track mt-1.5 h-2 overflow-hidden rounded-full">
            <div
              className="progress-bar h-2 rounded-full transition-all"
              style={{ width: `${dossier.completude.pourcentage}%` }}
            />
          </div>
        </div>

        <div className="space-y-2">
          {dossier.jalons.map((jalon) => (
            <div key={jalon.libelle} className="flex items-center gap-3">
              <div
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                  jalon.atteint
                    ? 'bg-codialis-mint/25 text-codialis-accent-texte'
                    : 'bg-gray-100 text-codialis-gray'
                }`}
              >
                {jalon.atteint ? '✓' : '·'}
              </div>
              <div
                className={`text-sm ${
                  jalon.atteint ? 'font-medium text-codialis-navy' : 'text-codialis-gray'
                }`}
              >
                {jalon.libelle}
              </div>
            </div>
          ))}
        </div>

        {/* Un dossier basculé du CFA vers l'OF garde son contrat en base : le
            passé du dossier n'est pas réécrit. Mais la période d'essai,
            l'échéance des 5 mois et la date d'examen ne pilotent plus rien en
            OF, et les afficher laisserait croire à un suivi qui n'a plus lieu
            (CDC §2.2). Le calendrier est propre au CERFA du contrat en cours
            (CDC §4.5) : la date d'examen est calculée à 1 mois avant la fin de
            contrat, jamais ressaisie (CDC phase 1, `resoudreDateExamen`). */}
        {!of && dossier.contrat && (
          <div className="space-y-2 border-t border-gray-100 pt-4">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
              <Icone nom="calendrier" className="h-3.5 w-3.5" />
              Calendrier{dossier.contrat.modeleCerfa ? ` · ${dossier.contrat.modeleCerfa}` : ''}
            </div>
            <dl className="space-y-1 text-xs">
              <div className="flex items-center justify-between gap-2">
                <dt className="text-codialis-gray">Contrat</dt>
                <dd className="font-medium text-codialis-navy">
                  {dateCourte(dossier.contrat.dateDebut)} → {dateCourte(dossier.contrat.dateFin)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-codialis-gray">Fin de période d’essai</dt>
                <dd className="font-medium text-codialis-navy">
                  {dateCourte(dossier.contrat.finPeriodeEssai)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-codialis-gray">Échéance des 5 mois</dt>
                <dd className="font-medium text-codialis-navy">
                  {dateCourte(dossier.contrat.echeance5Mois)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-codialis-gray">Date d’examen</dt>
                <dd className="font-medium text-codialis-navy">
                  {dateCourte(dossier.dossier.dateExamen)}
                </dd>
              </div>
              {dossier.contrat.rupture && (
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-codialis-gray">Rupture</dt>
                  <dd className="font-medium text-codialis-navy">
                    {dossier.contrat.rupture.dateRupture
                      ? dateCourte(dossier.contrat.rupture.dateRupture)
                      : 'À préciser'}
                    {dossier.contrat.rupture.formationMaintenue ? ' · formation maintenue' : ''}
                  </dd>
                </div>
              )}
              {/* Six mois au plus après la rupture (art. L6222-18-2), calculés
                  par l'API. Sans date de rupture, le décompte part de la
                  déclaration, et l'écran le dit. */}
              {dossier.contrat.finMaintien && (
                <div className="flex items-start justify-between gap-2">
                  <dt className="text-codialis-gray">Fin du maintien en formation</dt>
                  <dd className="text-right font-medium text-codialis-navy">
                    {dateCourte(dossier.contrat.finMaintien.fin)}
                    {!dossier.contrat.finMaintien.dateRuptureConnue && (
                      <span className="block text-[11px] font-normal text-codialis-gray">
                        depuis la déclaration, date de rupture à préciser
                      </span>
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>
    </Carte>
  );
}

function AlertesActives({ dossier }: { dossier: Dossier }): JSX.Element {
  return (
    <Carte titre="Alertes actives">
      {dossier.alertes.length === 0 ? (
        <p className="px-4 sm:px-6 py-4 text-sm text-codialis-gray">
          Aucune alerte ouverte sur ce dossier.
        </p>
      ) : (
        dossier.alertes.map((alerte) => (
          <LigneAlerte
            key={alerte.id}
            titre={alerte.libelle}
            detail={alerte.message || alerte.declencheur}
            ton="warning"
          />
        ))
      )}
    </Carte>
  );
}

/** Pourquoi aucune relance n'est attendue, dit en clair. */
const MOTIFS_SUSPENSION: Record<MotifSuspensionRelance, string> = {
  DOSSIER_COMPLET: 'Toutes les pièces sont déposées : plus rien à réclamer au candidat.',
  SCENARIO_INACTIF:
    'Le scénario « Pièces non déposées » est désactivé, ou ne couvre pas cette antenne.',
  LIEN_NON_ENVOYE:
    'Le lien d’accès à l’espace candidat n’a pas encore été envoyé : la cadence n’a pas de point de départ.',
  // La cadence automatique et l'envoi manuel n'ont pas le même périmètre de
  // statuts : un dossier en formation sort de la cadence, mais reste joignable
  // depuis l'écran des relances. Le dire, pour ne pas laisser croire que plus
  // rien n'est possible.
  STATUT_HORS_RELANCE:
    'Le statut du dossier sort de la cadence automatique. Une relance manuelle reste possible depuis l’écran des relances.',
  SANS_COORDONNEES:
    'Aucune adresse e-mail exploitable sur ce dossier : la relance ne peut pas partir.',
  SANS_DESTINATAIRE:
    'Aucun responsable en charge, et aucun compte administratif sur l’antenne : la relance ne peut partir à personne.',
  CADENCE_TERMINEE: 'La fréquence réglée ne prévoit plus de relance pour ce dossier.',
};

const LIBELLES_CANAL: Record<CanalEnvoi, string> = {
  EMAIL: 'Mail',
  SMS: 'SMS',
  IN_APP: 'Notification',
};

const PASTILLE_ISSUE: Record<IssueEnvoi, { texte: string; ton: TonPastille }> = {
  ENVOYE: { texte: 'Envoyé', ton: 'success' },
  JOURNALISE: { texte: 'Tracé, non expédié', ton: 'warning' },
  ECHEC: { texte: 'Échec', ton: 'danger' },
};

/**
 * Relances du dossier (CDC §4.1, §5.5).
 *
 * Répond à la question que le secrétariat se pose devant un dossier
 * incomplet : **quand part la prochaine relance**, et qu'est-ce qui est déjà
 * parti. La date vient de l'API, calculée par la même fonction que le passage
 * automatique : l'écran ne rejoue pas la cadence de son côté.
 */
function RelancesDuDossier({ dossier }: { dossier: Dossier }): JSX.Element {
  const relances = dossier.relances;
  const smsRetenuNonExpedie =
    relances.canaux.includes('SMS') && !relances.canauxExpedies.includes('SMS');

  return (
    <Carte titre="Relances des pièces" sansBordure>
      <div className="space-y-4 p-4 sm:p-6">
        {/* La réponse à la question, en tête et en gros. */}
        {relances.prochaineLe ? (
          <div className="rounded-xl bg-codialis-sand p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
              Prochaine relance
            </div>
            <div className="font-display mt-0.5 text-xl font-bold text-codialis-navy">
              {dateCourte(relances.prochaineLe)} à {heureReunion(relances.prochaineLe)}
            </div>
            <p className="mt-1 text-xs text-codialis-gray">
              {relances.piecesADeposer} pièce{relances.piecesADeposer > 1 ? 's' : ''} encore à
              déposer. Départ au passage automatique de l’heure, heure de La Réunion.
            </p>
          </div>
        ) : (
          <div className="rounded-xl bg-gray-50 p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
              Aucune relance prévue
            </div>
            <p className="mt-1 text-xs text-codialis-gray">
              {relances.motifSuspension
                ? MOTIFS_SUSPENSION[relances.motifSuspension]
                : 'Aucune relance n’est attendue sur ce dossier.'}
            </p>
          </div>
        )}

        <dl className="space-y-2 text-xs">
          <div className="flex items-start justify-between gap-3">
            <dt className="text-codialis-gray">Cadence</dt>
            <dd className="text-right font-medium text-codialis-navy">{relances.cadence}</dd>
          </div>
          <div className="flex items-start justify-between gap-3">
            <dt className="text-codialis-gray">Destinataires</dt>
            <dd className="text-right font-medium text-codialis-navy">
              {relances.libelleDestinataires}
              {relances.responsable &&
                ` · ${relances.responsable.prenom} ${relances.responsable.nom}`}
            </dd>
          </div>
          <div className="flex items-start justify-between gap-3">
            <dt className="text-codialis-gray">Canaux</dt>
            <dd className="flex flex-wrap justify-end gap-1">
              {relances.canaux.length === 0 ? (
                <span className="text-codialis-navy">aucun</span>
              ) : (
                relances.canaux.map((canal) => (
                  <Pastille
                    key={canal}
                    texte={LIBELLES_CANAL[canal]}
                    ton={relances.canauxExpedies.includes(canal) ? 'success' : 'warning'}
                  />
                ))
              )}
            </dd>
          </div>
          <div className="flex items-start justify-between gap-3">
            <dt className="text-codialis-gray">Lien d’accès envoyé</dt>
            <dd className="text-right font-medium text-codialis-navy">
              {dateCourte(relances.lienEnvoyeLe)}
            </dd>
          </div>
        </dl>

        {relances.sansResponsable && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-codialis-navy">
            Aucun responsable en charge de ce dossier : la relance part au pôle administratif de
            l’antenne. Désignez un conseiller référent pour qu’elle lui revienne.
          </p>
        )}

        {smsRetenuNonExpedie && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-codialis-navy">
            Le canal SMS est retenu sur le scénario mais n’est pas raccordé : ces envois sont
            tracés sans quitter la plateforme.
          </p>
        )}
      </div>

      {/* Historique : ce qui est réellement parti, canal par canal. */}
      <div className="border-t border-gray-100">
        <div className="px-4 sm:px-6 pb-2 pt-4 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
          Relances déjà parties
        </div>
        {relances.envois.length === 0 ? (
          <p className="px-4 sm:px-6 pb-5 text-sm text-codialis-gray">
            Aucune relance n’a encore été envoyée sur ce dossier.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {relances.envois.map((envoi) => (
              <li key={envoi.id} className="px-4 sm:px-6 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-codialis-navy">
                    {dateCourte(envoi.creeLe)} · {LIBELLES_CANAL[envoi.canal]}
                  </span>
                  <div className="flex flex-wrap items-center gap-1">
                    <Pastille
                      texte={envoi.pour === 'RESPONSABLE' ? 'Responsable' : 'Candidat'}
                      ton={envoi.pour === 'RESPONSABLE' ? 'warning' : 'neutral'}
                    />
                    <Pastille
                      texte={envoi.automatique ? 'Automatique' : 'Manuelle'}
                      ton={envoi.automatique ? 'info' : 'neutral'}
                    />
                    <Pastille
                      texte={PASTILLE_ISSUE[envoi.issue].texte}
                      ton={PASTILLE_ISSUE[envoi.issue].ton}
                    />
                  </div>
                </div>
                <div className="mt-0.5 break-all text-[11px] text-codialis-gray">
                  {envoi.destinataire}
                  {envoi.scenario ? ` · ${envoi.scenario}` : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Carte>
  );
}

function Remuneration({ dossier }: { dossier: Dossier }): JSX.Element | null {
  const remuneration = dossier.remuneration;
  if (!remuneration) return null;

  return (
    <Carte titre={`Rémunération légale · ${remuneration.tranche}`} sansBordure>
      <div className="space-y-2 p-4 sm:p-6">
        {remuneration.colonnes.map((colonne, index) => {
          const valeur = remuneration.valeurs[index];
          if (!valeur) return null;
          return (
            <div
              key={colonne}
              className="flex items-center justify-between rounded-lg bg-codialis-sand px-3 py-2 text-xs"
            >
              <span className="text-codialis-gray">{colonne}</span>
              <span className="text-codialis-navy">
                <span className="font-semibold">{valeur.pourcentage}</span> du SMIC ·{' '}
                <strong className="text-codialis-teal">{valeur.montant}</strong>
              </span>
            </div>
          );
        })}
        <p className="pt-1 text-[11px] text-codialis-gray">
          Barème {remuneration.bareme.toLowerCase()} au 01/06/2026. {remuneration.note}
        </p>
      </div>
    </Carte>
  );
}

/**
 * Écran ouvert sans dossier désigné. Il n'y a rien à charger : la personne est
 * renvoyée vers la liste depuis laquelle un dossier se choisit — celle du
 * portefeuille pour les pôles, l'espace personnel pour le candidat.
 */
function FicheDePlacement({
  fiche,
  versDossier,
}: {
  fiche: FichePlacement;
  versDossier: boolean;
}): JSX.Element {
  const [cvOuvert, setCvOuvert] = useState<number | null>(null);
  const [compteRendu, setCompteRendu] = useState<ImmersionDuDossier | null>(null);
  const fichiersCv = fiche.cv?.fichiers ?? [];
  const pieceCvId = fiche.cv?.pieceId;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre={`Candidat · ${fiche.prenom} ${fiche.nom}`}
        sousTitre="Relation entreprise · informations de placement"
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <Carte titre="Candidat">
            <dl className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-6">
              <ChampFiche libelle="Nom" valeur={fiche.nom} />
              <ChampFiche libelle="Prénom" valeur={fiche.prenom} />
              <ChampFiche
                libelle="Formation"
                valeur={fiche.formation ? `${fiche.formation.titre} (${fiche.formation.code})` : '—'}
              />
              <div className="min-w-0">
                <dt className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                  Statut
                </dt>
                <dd className="mt-1">
                  <Pastille texte={fiche.statutLibelle} ton={fiche.statutTon} />
                </dd>
              </div>
            </dl>
          </Carte>

          <Carte titre="CV">
            {fichiersCv.length === 0 ? (
              <p className="p-4 text-sm text-codialis-gray sm:p-6">Aucun CV déposé au dossier.</p>
            ) : (
              fichiersCv.map((fichier, rang) => (
                <div
                  key={fichier.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Icone nom="document" className="h-5 w-5 shrink-0 text-codialis-sky" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-codialis-navy">
                        {fichier.nomFichier}
                      </div>
                      <div className="text-[11px] text-codialis-gray">
                        Déposé le {dateCourte(fichier.deposeLe)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCvOuvert(rang)}
                    className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] shrink-0 items-center gap-1.5 rounded-lg border border-codialis-navy/20 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-codialis-sky"
                  >
                    <Icone nom="oeil" className="h-3.5 w-3.5" />
                    Voir le CV
                  </button>
                </div>
              ))
            )}
          </Carte>

          {fiche.immersions.length > 0 && (
            <PeriodesImmersion immersions={fiche.immersions} onVoirCompteRendu={setCompteRendu} />
          )}
        </div>

        <div className="min-w-0 space-y-6">
          <EntrepriseRattachee
            entreprise={fiche.entreprise}
            immersions={fiche.immersions}
            rechercheEntreprise={fiche.rechercheEntreprise}
            rechercheDepuisLe={fiche.rechercheDepuisLe}
            of={fiche.mode === 'OF'}
            versDossier={versDossier}
          />
        </div>
      </div>

      {cvOuvert !== null && pieceCvId && (
        <VisionneusePiece
          ouverte
          libellePiece={`CV · ${fiche.prenom} ${fiche.nom}`}
          fichiers={fichiersCv}
          indexInitial={cvOuvert}
          urlFichier={(fichierId) => urlCv(pieceCvId, fichierId)}
          onFermer={() => setCvOuvert(null)}
        />
      )}

      {compteRendu && (
        <VisionneusePiece
          ouverte
          libellePiece="Compte rendu d’entretien en entreprise"
          nomFichier={`compte-rendu-entretien-entreprise-${compteRendu.id}.pdf`}
          urlBase={urlCompteRenduImmersion(compteRendu.id)}
          onFermer={() => setCompteRendu(null)}
        />
      )}
    </div>
  );
}

/**
 * Lien vers le bloc Financement du dossier (TF-464), pour qui a « Financeurs &
 * OPCO » : le bloc est bas dans la page, sous les pièces.
 */
function LienFinancement({ apprenantId }: { apprenantId: string }): JSX.Element {
  return (
    <Link
      to={`/adm-dossier/${apprenantId}#financement`}
      className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
    >
      <Icone nom="euro" className="h-4 w-4" />
      Financement
    </Link>
  );
}

function ChampFiche({ libelle, valeur }: { libelle: string; valeur: string }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">{libelle}</dt>
      <dd className="mt-1 break-words text-sm font-medium text-codialis-navy">{valeur}</dd>
    </div>
  );
}

function SansDossier({
  versLaListe,
  versInscriptions,
}: {
  versLaListe: boolean;
  /** Seuls les profils qui inscrivent ont l'écran : les autres y seraient refusés. */
  versInscriptions: boolean;
}): JSX.Element {
  return (
    <div className="mx-auto max-w-3xl p-4 md:p-6">
      <div className="rounded-2xl bg-white p-4 sm:p-8 text-center shadow-card md:p-10">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-codialis-sand">
          <Icone nom="dossier" className="h-6 w-6 text-codialis-gray" />
        </div>
        <h1 className="font-display text-lg font-bold text-codialis-navy">
          Aucun dossier sélectionné
        </h1>
        <p className="mx-auto mt-1 max-w-lg text-sm text-codialis-gray">
          {versLaListe
            ? 'Le dossier apprenant s’ouvre depuis une liste : choisissez la personne dont vous voulez consulter le dossier.'
            : 'Vos documents et votre dossier sont réunis dans votre espace personnel.'}
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          {versLaListe ? (
            <>
              <Link
                to="/learners"
                className="min-h-[44px] rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
              >
                Tous les apprenants
              </Link>
              {versInscriptions && (
                <Link
                  to="/adm-inscriptions"
                  className="min-h-[44px] rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
                >
                  Inscriptions &amp; entretiens
                </Link>
              )}
            </>
          ) : (
            <Link
              to="/app-documents"
              className="min-h-[44px] rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
            >
              Mes documents
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Périodes d'immersion du dossier (CDC §4.2), et leur compte rendu d'entretien.
 *
 * Le dossier ne stocke pas le document : il désigne l'immersion, et la route
 * gardée sert le même fichier que le dossier de l'entreprise et que l'écran des
 * périodes d'immersion. Une immersion sans compte rendu le dit, plutôt que de
 * laisser une ligne muette.
 */
function PeriodesImmersion({
  immersions,
  onVoirCompteRendu,
}: {
  immersions: ImmersionDuDossier[];
  /** Absent pour qui n'a pas la route du compte rendu : la date reste lisible. */
  onVoirCompteRendu?: (immersion: ImmersionDuDossier) => void;
}): JSX.Element {
  return (
    <Carte titre="Périodes d’immersion en entreprise">
      <div className="divide-y divide-gray-50">
        {immersions.map((immersion) => (
          <div
            key={immersion.id}
            className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4"
          >
            <div className="flex min-w-0 items-center gap-3">
              <Icone nom="document" className="h-5 w-5 shrink-0 text-codialis-sky" />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-codialis-navy">
                  {immersion.entreprise}
                </div>
                <div className="text-[11px] text-codialis-gray">
                  {periodePositionnement(immersion, dateCourte)}
                  {immersion.compteRendu
                    ? ` · compte rendu d’entretien en entreprise du ${dateCourte(
                        immersion.compteRendu.dateVisite,
                      )}`
                    : immersion.sansImmersion
                      ? ''
                      : ' · aucune évaluation réalisée'}
                </div>
              </div>
            </div>
            {immersion.compteRendu ? (
              onVoirCompteRendu ? (
              <button
                type="button"
                onClick={() => onVoirCompteRendu(immersion)}
                className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] shrink-0 items-center gap-1.5 rounded-lg border border-codialis-navy/20 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-codialis-sky"
              >
                <Icone nom="oeil" className="h-3.5 w-3.5" />
                Voir
              </button>
              ) : null
            ) : immersion.sansImmersion ? null : (
              <span className="shrink-0 text-[11px] text-codialis-gray">
                Aucune évaluation réalisée
              </span>
            )}
          </div>
        ))}
      </div>
    </Carte>
  );
}
